/* global google */

import * as I from "immutable";
import moment from "moment-timezone";
import Raven from "raven-js";

import {MAX_NUMBER_OF_WAYPOINTS} from "config/constants";
import {combineDateTime} from "utils";

/**
 * Split Schedule list into chunks
 *
 * Notice: LAST element of `N` chunk equals to the FIRST element of chunk `N+1`
 * this is made to get driving time between first and last elements of
 * different chunks
 */
const splitScheduleIntoChunks = (list, numberOfChunks) => {
  const res = [];
  for (let i = 0; i < numberOfChunks; i++) {
    const start = i * (MAX_NUMBER_OF_WAYPOINTS - 1);
    const end = start + MAX_NUMBER_OF_WAYPOINTS;
    res.push(list.slice(start, end));
  }
  return res;
};

export const accountForDrivingTimeInDestinations = (startTime, destinations, responses) => {
  if (destinations.filter(addressArrow).length < 2) {
    return destinations;
  }

  const legs = responses.reduce((res, chunkResponse) =>
        res.concat(chunkResponse.routes[0].legs),
        []
    );
  const durations = legs.map(item => {
    const mVisitDuration = moment.duration(item.duration.value, "seconds");
    return moment.duration(Math.ceil(mVisitDuration.asMinutes()), "minutes");
  });

  let durationIdx = 0;
  let prevTime;

  return destinations.map((item, idx) => {
    if (idx && item.address) {
      item.estimatedStartTime = combineDateTime(
                prevTime, durations[durationIdx]);
      durationIdx++;
    } else if (idx) {
      item.estimatedStartTime = prevTime;
    } else {
      item.estimatedStartTime = startTime;
    }

    item.estimatedEndTime = combineDateTime(
            item.estimatedStartTime, item.duration);
    prevTime = item.estimatedEndTime;

    return item;
  }, []);
};

export const addressArrow = item => item.address;

/**
 * Wraps google.maps.DirectionsService api call in promise
 **/
export const directionServiceRoute = (requests) => {
  const directionsService = new google.maps.DirectionsService;
  return new Promise((resolve, reject) => {
    directionsService.route(requests, (result, status) => {
      if (status === google.maps.DirectionsStatus.OK) {
        resolve(result);
      } else {
        reject({status, requests});
        if (status !== google.maps.DirectionsStatus.OVER_QUERY_LIMIT) {
          Raven.captureException("Google Maps API error", {
            extra: {
              status,
              result,
              requests,
            },
          });
        }
      }
    });
  });
};

/**
 * Generate Google API Driving Time requests for V1 destinations Array.
 */
export const generateGoogleRequestsForDrivingTime = (destinations) => {
  const destinationsFiltered = destinations.filter(addressArrow);

  let chunks;
  if (destinationsFiltered.length < 2) {
    return;
  } else if (destinationsFiltered.length > MAX_NUMBER_OF_WAYPOINTS) {
    let numberOfChunks = parseInt(
            destinationsFiltered.length / MAX_NUMBER_OF_WAYPOINTS, 10);
    numberOfChunks = destinationsFiltered.size % MAX_NUMBER_OF_WAYPOINTS ?
            numberOfChunks + 1 :
            numberOfChunks;
    chunks = splitScheduleIntoChunks(destinationsFiltered, numberOfChunks);
  } else {
    chunks = [destinationsFiltered];
  }

  return chunks.map(chunk => ({
    origin: chunk[0].address,
    destination: chunk[chunk.length - 1].address,
    waypoints: getRouteWaypoints(chunk),
    travelMode: google.maps.TravelMode.DRIVING,
  }));
};

/**
 * Get Route waypoints from JS Array or Immutable List.
 */
export const getRouteWaypoints = (destinations) => {
  const isList = I.List.isList(destinations);
  return destinations
        .slice(1, (isList ? destinations.size : destinations.length) - 1)
        .map(item => {
          return {location: {query: item.address}};
        });
};

export default {
  accountForDrivingTimeInDestinations,
  addressArrow,
  directionServiceRoute,
  generateGoogleRequestsForDrivingTime,
  getRouteWaypoints,
};



/** WEBPACK FOOTER **
 ** ./src/app/v1/googleUtils.js
 **/